import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { useModalHosts, useToasts } from './toast-store';
import styles from './ui.module.css';

/**
 * Polite live region; errors are announced assertively. While a modal dialog is open the
 * toasts render inside it: the dialog is in the top layer, so no z-index can lift them over it.
 */
export function Toaster() {
  const { toasts, dismiss } = useToasts();
  const host = useModalHosts((s) => s.hosts[s.hosts.length - 1]);
  const { t } = useTranslation();
  const region = (
    <div className={styles.toaster}>
      <div aria-live="polite" role="status">
        {toasts
          .filter((x) => x.kind !== 'error')
          .map((x) => (
            <ToastView
              key={x.id}
              toast={x}
              onDismiss={() => dismiss(x.id)}
              closeLabel={t('common.dismiss')}
            />
          ))}
      </div>
      <div aria-live="assertive" role="alert">
        {toasts
          .filter((x) => x.kind === 'error')
          .map((x) => (
            <ToastView
              key={x.id}
              toast={x}
              onDismiss={() => dismiss(x.id)}
              closeLabel={t('common.dismiss')}
            />
          ))}
      </div>
    </div>
  );
  return host ? createPortal(region, host) : region;
}

function ToastView({
  toast,
  onDismiss,
  closeLabel,
}: {
  toast: ReturnType<typeof useToasts.getState>['toasts'][number];
  onDismiss(): void;
  closeLabel: string;
}) {
  return (
    <div className={[styles.toast, styles[`toast-${toast.kind}`]].join(' ')}>
      <Icon name={toast.kind === 'error' ? 'close' : 'check'} />
      <span className={styles.toastMessage}>{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className={styles.toastAction}
          onClick={() => {
            toast.action!.run();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className={styles.toastClose}
        onClick={onDismiss}
        aria-label={closeLabel}
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
